

import { useState, useEffect, useContext } from "react";
import Select from "react-select";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Colors, MediaQueries, Spacing } from "@paclife/plds.tokens";
import { Button } from "@paclife/plds.button";
import validator from "utils/validator";
import API from "api/api";
import { AppContext } from "context/AppContext";
import Typography from "common/Typography/Typography";
import BaseModal from "common/Modal/BaseModal";
import TextField from "common/TextField/TextField";
import Message from "common/Message/Message";
import { COUNTRIES, PHONE_TYPE } from "../../constants";

const Content = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;

    .factor-value {
        max-width: 350px;
    }
    .mfa-code {
        max-width: 200px;
    }
`;

const Title = styled(Typography)`
    color: ${Colors.primary};
    margin: ${Spacing[16]} 0 ${Spacing[24]};
    // tablet and mobile
    @media (max-width: ${MediaQueries.lg}) {
        margin: ${Spacing[8]} 0;
    }
`;

const SubTitle = styled(Typography)`
    margin-bottom: ${Spacing[16]};
`;

const CancelButton = styled(Button)`
    width: 108px;
`;
const ConfirmButton = styled(Button)`
    width: 108px;
`;

const ResendCode = styled.div`
    margin-top: ${Spacing[16]};
`;

const ResendButton = styled(Button)`
    margin-left: ${Spacing[8]};
`;

const CountryDropdown = styled(Select)`
    display: block;
    z-index: 1000;
    width: 60%;
    min-width: 160px;
`
const CountryCode = styled.div`
    width: 56px;
    height: 48px;
    padding-top: 13px;
    border: 2px solid rgb(222, 222, 222);
    border-right: 0;
    border-radius: 4px 0 0 4px;
    color: rgb(150, 150, 150);
    text-align: center;
    background-color: rgb(251, 251, 251);
`;

const PhoneTypeDropDown = styled(Select)`
    display: block;
    width: 60%;
    min-width: 160px;
`
const PhoneNumber = styled.div`
    display: flex;
    margin-top: 15px;
    width: 60%;
    min-width: 220px;
`;

const ContentCont = styled(Typography)`
    margin-top: ${Spacing[16]};
    margin-bottom: ${Spacing[4]};
    text-align: left;

`;


const MFAModal = ({
    closeModal,
    factorType,
    factorId,
    subType,
    defaultValue,
    defaultScreen
}) => {
    const { t } = useTranslation(["mfaModal"]);
    const {
        fetchProfileData,
        dispatch,
        phoneData,
        account: { primaryEmail, secondaryEmail }
    } = useContext(AppContext);

    const [screen, setScreen] = useState(defaultScreen);
    const [factorValue, setFactorValue] = useState(
        defaultScreen === "verify" ? defaultValue : ""
    );
    const [verifyEmail, setVerifyEmail] = useState("");
    const [verifyEmailError, setVerifyEmailError] = useState(false);
    const [code, setCode] = useState("");
    const [resendCode, setResendCode] = useState(false);
    const [loading, setLoading] = useState(false);

    const [formatError, setFormatError] = useState("");
    const [updateError, setUpdateError] = useState("");
    const [verifyError, setVerifyError] = useState("");

    const [updateDisabled, setUpdateDisabled] = useState(true);
    const [verifyDisabled, setVerifyDisabled] = useState(true);
    
    const [selectedCountry, setSelectedCountry] = useState("United States");
    const [countryCode, setCountryCode] = useState("+1");
    const [phoneWithCountryCode, setPhoneWithCountryCode] = useState("");
    const [phoneExtension, setPhoneExtension] = useState("");
    const [selectedPhoneType, setSelectedPhoneType] = useState({ value: 'sms', label: 'Text/SMS' });
    const [verifyPhoneType, setVerifyPhoneType] = useState("sms");
    const [challengeId, setChallengeId] = useState("");
    
    const [newNumberFactorId, setNewNumberFactorId] = useState("");
    const [extDisplay, setExtDisplay] = useState(false);

    let fType = "sms";
    let codeMask = {
        mask: [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
        guide: false
    };

    if (verifyPhoneType === "call") {
        codeMask = {
            mask: [/\d/, /\d/, /\d/, /\d/, /\d/],
            guide: false
        };
    }

    const handleChangeVerifyEmail = (e) => {
        setVerifyEmail(e.target.value);
    }

    useEffect(() => {
        if (subType === "swap") {
            swapPrimaryAndSecondary();
        }
    }, [subType]);

    useEffect(() => {
        if (!factorValue.split('').includes("+")) {
            setPhoneWithCountryCode(`${countryCode}${factorValue}`);
        }
    }, [factorValue]);

    useEffect(() => {
        // if (factorType === "phone") {
        //     //fType = getVerifyType();
        //     //setVerifyPhoneType(fType);
        //     // console.log('verifyPhoneType', verifyPhoneType);
        //     // console.log('selectedPhoneType', selectedPhoneType);
        // } 
        if (defaultScreen === "verify") {
            handleResend();
        }  
    }, [phoneData]);

    const getVerifyType = () => {        
        for (const element of phoneData) { 
            // unverified or new number  
            if (element.id === factorId || factorId === "" ) { 
                if (element.id.includes('clf')) {
                    fType = "call"  
                } else {
                    fType = "sms"
                }   
            }
        } 
        return fType;
    }

    // keep temp var for swapping
    const [pEmail] = useState(primaryEmail);
    const [sEmail] = useState(secondaryEmail);

    async function swapPrimaryAndSecondary() {
        setLoading(true);

        const { succeed: pSucceed } = await API.updateProfile({
            type: "primaryEmail",
            emailAddress: sEmail
        });
        if (pSucceed) {
            dispatch({
                type: "CONTACT",
                payload: { primaryEmail: sEmail }
            });
        }
        const { succeed: sSucceed } = await API.updateProfile({
            type: "secondaryEmail",
            emailAddress: pEmail
        });

        setLoading(false);

        if (sSucceed) {
            dispatch({
                type: "CONTACT",
                payload: { secondaryEmail: pEmail }
            });
        }
        closeModal();
    }

    async function handleResend() {
        setLoading(true);
        const response = await API.resendFactorCode({
            factorType,
            factorValue: subType === "swap" ? sEmail : factorValue,
            factorId,
            verifyType: factorType === 'phone' ? verifyPhoneType : "email",
            challengeId: challengeId
          
        });
        setLoading(false);
        if (response.succeed) {
            dispatch({
                type: "FACTOR",
                payload: {
                    email: {
                        id: null,
                        factorValue,
                        status: "PENDING_ACTIVATION"
                    }
                }
            });
            setVerifyError("");
        } else {
            setVerifyError(response.message);
        }
    }

    useEffect(() => {
        let timer;
        if (screen === "verify") {
            setTimeout(() => {
                setResendCode(true);
            }, 30000);
        }
        return () => clearTimeout(timer);
    }, [screen]);

    const handleUpdateError = () => {
        if (defaultValue !== "" && factorValue === defaultValue) {
            setUpdateError(
                t(
                    `mfaModal:${screen}.${factorType}${
                        subType ? `.${subType}` : ""
                    }.errorMessage`
                )
            );
        } else if (
            subType === "primary" &&
            secondaryEmail !== "" &&
            factorValue === secondaryEmail
        ) {
            setUpdateError(t(`mfaModal:update.email.primary.errorMessageSame`));
        } else if (subType === "secondary" && factorValue === primaryEmail) {
            setUpdateError(t(`mfaModal:update.email.secondary.errorMessageSame`));
        } else {
            setUpdateError("");
        }
    };

    const handleUpdateBtnState = () => {
        if (factorValue === "") {
            setUpdateDisabled(true);
        } else if (factorValue === defaultValue) {
            setUpdateDisabled(true);
        } else if (subType === "primary" && factorValue === secondaryEmail) {
            setUpdateDisabled(true);
        } else if (subType === "secondary" && factorValue === primaryEmail) {
            setUpdateDisabled(true);
        } else if (formatError) {
            setUpdateDisabled(true);
        } else if (factorType === "email" && verifyEmailError) {
            setUpdateDisabled(true);
        } else {
            setUpdateDisabled(false);
        }
    };

    useEffect(() => {
        if (factorType === "email") {
            if (verifyEmail === factorValue) {
                setVerifyEmailError(false);
            } else {
                setVerifyEmailError(true);
            }
        }
        if (screen === "update") {
            handleUpdateError();
            handleUpdateBtnState();
        }
    }, [factorValue, verifyEmail, verifyEmailError]);

    useEffect(() => {
        if (screen === "verify") {
            let len = 6;
            if (getVerifyType() === "call") {
                len = 5;
            }
            if (code.length < len) {
                setVerifyDisabled(true);
            } else {
                setVerifyDisabled(false);
            }
        }
    }, [code]);

    const validateFormat = (factorType, value) => {
        const phoneRegex = /^\d{10}$/;
        switch (factorType) {
            case "email":
                return validator.Email(value);
            case "phone":
                return phoneRegex.test(value);
            default:
                throw new Error(`Unhandled factor type: ${factorType}`);
        }
    };

    const handleChange = (e) => {
        setFactorValue(e.target.value);
        const valid = validateFormat(factorType, e.target.value);
        if (valid) {
            setFormatError(false);
        } else {
            setFormatError(true);
        }
    };

    const handleExtensionChange = (e) => {
        setPhoneExtension(e.target.value);
    }

    const handleCancel = () => {
        if (screen === "update") {
            closeModal();
        } else if (defaultScreen === "verify") {
            closeModal();
        } else {
            setUpdateError("");
            setScreen("update");
        }
    };

    const updateSecondaryEmail = async () => {
            //secondary can't be same as primary 
            if (factorValue === pEmail) { 
                setUpdateError(t(`mfaModal:update.email.primary.errorMessage`));
                return;
            }
        setLoading(true);
        const response = await API.updateProfile({
            type: "secondaryEmail",
            emailAddress: factorValue
        });

        setLoading(false);
        if (!response.succeed) {
            setUpdateError(response.message);
            return;
        }

        dispatch({
            type: "CONTACT",
            payload: { secondaryEmail: factorValue }
        });
        closeModal();
        fetchProfileData();
    };

    const updatePrimaryEmail = async () => { 
        //primary can't be same as secondary
        if (factorValue === sEmail) { 
            setUpdateError(t(`mfaModal:update.email.secondary.errorMessage`));
            return;
        }
        setLoading(true);
        const reqBody = { 
            factorType: 'primaryemail',
            factorValue: factorValue, 
            verifyType: "email", 
            factorId: factorId 
        };

        let response;
        if (!defaultValue) {
            response = await API.createFactor(reqBody);
        } else {
            response = await API.updateFactor(reqBody);
        }
        if (!response.succeed) {
            setLoading(false);
            setUpdateError(response.message);
            return;
        } 
        const { status } = response.data.data;
        setLoading(false);
        setChallengeId(response.data.data.challengeId);
        setNewNumberFactorId(response.data.factorId);
            
        dispatch({
            type: "FACTOR",
            payload: {
                [factorType]: { id: null, factorValue, status }
            }
        });
    
        fetchProfileData();
        if (status === "ACTIVE") {
            closeModal();
        } else {
            setScreen("verify");
        }
    };

    const updatePhoneFactor = async () => {
        setLoading(true);
        let fValue;
        if (verifyPhoneType === "call") {
            if (phoneExtension) {
                fValue = `${countryCode + factorValue}-${phoneExtension}`;
            } else {
                fValue = countryCode + factorValue;
            }
        } else if (verifyPhoneType === "sms") {
            fValue = countryCode + factorValue;
        } else {
            fValue = factorValue;
        }

        const reqBody = { 
            factorType, 
            factorValue: fValue, 
            verifyType: verifyPhoneType, 
            factorId: factorId };
        let response;
        if (!defaultValue) {
            response = await API.createFactor(reqBody);
        } else {
            response = await API.updateFactor(reqBody);
        }
        if (!response.succeed) {
            setLoading(false);
            setUpdateError(response.message);
            return;
        }
        const {
            succeed,
            data: {
                data: { profileFactors }
            },
            message
        } = await API.getFactor(factorType);
        setLoading(false);
        if (!succeed) {
            setUpdateError(message);
            return;
        }
        const { status } = profileFactors[0];
        setNewNumberFactorId(profileFactors[0].id);

        dispatch({
            type: "FACTOR",
            payload: {
                [factorType]: { id: null, fValue, status }
            }
        });
    
        fetchProfileData();
        if (status === "ACTIVE") {
            closeModal();
        } else {
            setScreen("verify");
        }
    };

    const handleUpdate = async () => {
        let type = "";
        if (factorType === "email") {
            type = subType;
        } else {
            type = "number";
        }
        switch (type) {
            case "primary":
                updatePrimaryEmail();
                return;
            case "secondary":
                updateSecondaryEmail();
                return;
            case "number":
                updatePhoneFactor();
                return;
            default:
                throw new Error(`Unhandled type ${type}`);
        }
    };

    const handleVerify = async () => {
        setLoading(true);
        let val;
        if (subType === "swap") {
            val = sEmail;
        } else if (factorType === "email") {
            val = factorValue;
        } else if (verifyPhoneType === "call") {
            if (factorValue.split('').includes("+")) {
                if (phoneExtension) {
                    val = `${factorValue}-${phoneExtension}`;
                } else {
                    val = factorValue;
                }
            } else {
                if (phoneExtension) {
                    val = `${phoneWithCountryCode}-${phoneExtension}`;
                } else {
                    val = phoneWithCountryCode;
                }
            }
        } else if (verifyPhoneType === "sms") {
            if (factorValue.split('').includes("+")) {
                val = factorValue;
            } else {
                val = phoneWithCountryCode;
            }
        } else {
            val = factorValue;
        }
        
        const { succeed, message } = await API.activateFactor({
            factorType,
            factorId: newNumberFactorId, 
            factorValue: val,
            verifyType: factorType === 'email' ? 'email' : verifyPhoneType,
            activationCode: code,
            challengeId: challengeId
        });
        setLoading(false);

        if (!succeed) {
            setVerifyError(message);
        } else {
            dispatch({
                type: "FACTOR",
                payload: {
                    [factorType]: {
                        id: null,
                        factorValue: val,
                        status: "ACTIVE"
                    }
                }
            });
            fetchProfileData();
            closeModal();
        }
    };

    const handleCountryChange = (e) => {
        setCountryCode(e.value);
        setSelectedCountry(e.label);
        setPhoneWithCountryCode(`${e.value}${factorValue}`);
    }

    const handlePhoneTypeChange = (e) => {
        setSelectedPhoneType(e);
        setVerifyPhoneType(e.value);
        fType = e.value;
        (fType === "sms") ? setExtDisplay(false) : setExtDisplay(true); 
    }

    const tFactorType = factorType === 'phone' ? verifyPhoneType : factorType;
    const tKey = `${screen}.${tFactorType}${subType ? `.${subType}` : ""}`;
    const modalSize = factorType === "phone" ? "drop" : "default";

    return (
        <BaseModal
            size={modalSize}
            secondaryButton={
                <CancelButton
                    variant="secondaryNeutral"
                    onClick={handleCancel}
                    data-track="analytics"
                    data-bname="close-mfa-modal"
                >
                    {t(`mfaModal:${screen}.button.secondary`)}
                </CancelButton>
            }
            primaryButton={
                <ConfirmButton
                    onClick={screen === "update" ? handleUpdate : handleVerify}
                    disabled={
                        screen === "update" ? updateDisabled : verifyDisabled
                    }
                    data-track="analytics"
                    data-bname={screen}
                >
                    {t(`mfaModal:${screen}.button.primary`)}
                </ConfirmButton>
            }
            closeModal={closeModal}
            loading={loading}
        >
            <Content>
                {screen === "update" && (
                    <>
                        <Title variant="h2" display="block" align="center">
                            {t(`mfaModal:${tKey}.title`)}
                        </Title>
                        <SubTitle variant="body" align="center">
                            {t(`mfaModal:${tKey}.subtitle`)}
                        </SubTitle>
                        {factorType === "phone" && (
                           // (factorType === "call" || factorType === "sms") && (
                            <>
                                <CountryDropdown
                                    className="country-code"
                                    placeholder={selectedCountry}
                                    onChange={e => handleCountryChange(e)}
                                    options={COUNTRIES}
                                    data-track="analytics"
                                />
                                <PhoneNumber>
                                    <CountryCode>{countryCode}</CountryCode>
                                    <TextField
                                        id="factor-value"
                                        name="factorValue"
                                        value={factorValue}
                                        onChange={handleChange}
                                        error={formatError}
                                        errorMessage="Please enter correct format"
                                        style={{ borderRadius: "0 4px 4px 0" }}
                                        maxLength={10}
                                    />
                                    { extDisplay && ( 
                                        <TextField
                                            id="phone-extension"
                                            name="phoneExtension"
                                            value={phoneExtension}
                                            onChange={handleExtensionChange}
                                            maxLength={4}
                                            placeholder="extension"
                                            style={{
                                                marginLeft: "10px",
                                                width: "93%",
                                                minWidth: "90px"
                                            }}
                                        />
                                    )}
                                </PhoneNumber>
                                <ContentCont> {t(`mfaModal:update.phone.phoneType`)}</ContentCont>
                                <PhoneTypeDropDown
                                    className="phone-type"
                                    onChange={e => handlePhoneTypeChange(e)}
                                    value={selectedPhoneType}
                                    options={PHONE_TYPE}
                                    data-track="analytics"
                                />
                            </>
                        )}
                        {factorType === "email" && subType !== "swap" && (
                            <>
                                <TextField
                                    autoFocus
                                    id="email"
                                    name="email"
                                    value={factorValue}
                                    onChange={handleChange}
                                    error={formatError}
                                    errorMessage="Please enter correct format"
                                    placeholder="Enter your email address"
                                />
                                <TextField
                                    id="verifyEmail"
                                    name="verifyEmail"
                                    value={verifyEmail}
                                    onChange={handleChangeVerifyEmail}
                                    allowpaste={false}
                                    placeholder="Confirm your email address"
                                    error={verifyEmailError}
                                    errorMessage="Email addresses do not match"
                                    style={{ margin: "15px auto auto auto" }}
                                />
                            </>
                        )}
                        
                        {updateError && (
                            <Message type="error" message={updateError} />
                        )}
                    </>
                )}
                {screen === "verify" && (
                    <>
                        <Title variant="h2" display="block" align="center">
                            {t(`mfaModal:${tKey}.title`)}
                        </Title>
                        <SubTitle variant="body" align="center">
                            {t(`mfaModal:${tKey}.subtitle`)}
                        </SubTitle>
                        <TextField
                            className="mfa-code"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            maskedInputProps={codeMask}
                        />
                        {verifyError && (
                            <Message type="error" message={verifyError} />
                        )}
                        {resendCode && (
                            <ResendCode>
                                <Typography variant="microcopy">
                                    Didn't receive code?
                                </Typography>
                                <ResendButton
                                    variant="text"
                                    onClick={handleResend}
                                    size="small"
                                >
                                    Resend Code
                                </ResendButton>
                            </ResendCode>
                        )}
                    </>
                )}
            </Content>
        </BaseModal>
    );
};

export default MFAModal;

MFAModal.propTypes = {
    closeModal: PropTypes.func.isRequired,
    factorType: PropTypes.oneOf(["email", "sms", "call", "swap"]).isRequired,
    subType: PropTypes.oneOf(["primary", "secondary", "swap", ""]),
    defaultValue: PropTypes.string.isRequired,
    defaultScreen: PropTypes.string
};

MFAModal.defaultProps = {
    defaultScreen: "update",
    subType: ""
};
